import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import { Container, Row, Col, Form, Button, Card, Alert, Badge, Spinner } from 'react-bootstrap';
import { FiDownload, FiImage, FiLink, FiSave, FiUser } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const App = () => {
  console.log('QR App component loaded');
  const { user, isAdmin, loading: authLoading, apiBaseUrl, login, logout } = useAuth();
  const [url, setUrl] = useState('https://example.com');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [overlayImage, setOverlayImage] = useState(null);
  const [overlayImageUrl, setOverlayImageUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const generateQRCode = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      console.log('Generating QR code for:', url);
      
      // More flexible URL validation
      let validUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        validUrl = 'https://' + url;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas not found');
      }

      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = 400;
      canvas.height = 400;

      console.log('Canvas ready, generating QR code...');

      // Generate QR code
      await QRCode.toCanvas(canvas, validUrl, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // High error correction for image overlay
      });

      console.log('QR code generated successfully');

      // Add overlay image if provided
      if (overlayImage) {
        console.log('Adding overlay image...');
        await addOverlayImage(ctx, canvas);
      }

      // Convert to data URL for right-click saving
      const dataUrl = canvas.toDataURL('image/png');
      setQrCodeDataUrl(dataUrl);
      console.log('QR code data URL set');

    } catch (err) {
      console.error('QR code generation error:', err);
      setError('Failed to generate QR code: ' + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const addOverlayImage = (ctx, canvas) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Calculate overlay size (about 20% of QR code)
        const overlaySize = canvas.width * 0.2;
        const x = (canvas.width - overlaySize) / 2;
        const y = (canvas.height - overlaySize) / 2;

        // Add white background circle for better visibility
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, overlaySize / 2 + 5, 0, 2 * Math.PI);
        ctx.fill();

        // Draw the overlay image
        ctx.drawImage(img, x, y, overlaySize, overlaySize);
        resolve();
      };
      img.src = overlayImageUrl;
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setOverlayImage(file);
          setOverlayImageUrl(e.target.result);
          setError('');
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please select a valid image file');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setOverlayImage(file);
          setOverlayImageUrl(event.target.result);
          setError('');
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please drop a valid image file');
      }
    }
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-code-${Date.now()}.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  const saveQRCodeToAPI = async () => {
    if (!qrCodeDataUrl || !user) {
      setError('Cannot save QR code - please login first');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      // Convert data URL to blob
      const response = await fetch(qrCodeDataUrl);
      const blob = await response.blob();
      
      // Create form data
      const formData = new FormData();
      const filename = `qr-code-${url.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.png`;
      formData.append('file', blob, filename);
      formData.append('gameSubdomain', 'qr-generator'); // Special subdomain for QR codes
      
      // Save to API
      const saveResponse = await fetch(`${apiBaseUrl}/admin/files/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await saveResponse.json();
      
      if (data.success) {
        setSuccess(`QR code saved successfully as "${data.file.originalName}"`);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.message || 'Failed to save QR code');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Error saving QR code to API');
    } finally {
      setIsSaving(false);
    }
  };

  const clearOverlay = () => {
    setOverlayImage(null);
    setOverlayImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Generate initial QR code after component mounts
  useEffect(() => {
    if (canvasRef.current && url.trim()) {
      generateQRCode();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate QR code when URL changes (with debounce)
  useEffect(() => {
    if (url.trim() && canvasRef.current) {
      const timer = setTimeout(() => {
        generateQRCode();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setQrCodeDataUrl('');
    }
  }, [url, overlayImage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          <Card className="main-container border-0 p-4">
            <Card.Body>
              <div className="text-center mb-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div></div>
                  <div>
                    <h1 className="display-4 fw-bold text-primary mb-2">
                      QR Code Generator
                    </h1>
                    <p className="lead text-muted">
                      Create custom QR codes with optional image overlays
                    </p>
                  </div>
                  <div>
                    {authLoading ? (
                      <Spinner size="sm" />
                    ) : user ? (
                      <div className="d-flex flex-column align-items-end gap-1">
                        <Badge 
                          bg={isAdmin ? "success" : "primary"} 
                          className="d-flex align-items-center gap-1"
                        >
                          <FiUser size={12} />
                          {isAdmin ? "Admin" : "User"}: {user.name}
                        </Badge>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={logout}
                          className="px-2 py-1"
                          style={{ fontSize: '12px' }}
                        >
                          Logout
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex flex-column align-items-end gap-1">
                        <Badge bg="secondary">Guest User</Badge>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={login}
                          className="px-2 py-1"
                          style={{ fontSize: '12px' }}
                        >
                          Login
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-4">
                  {success}
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Header className="bg-transparent border-0 pb-0">
                      <h5 className="mb-0">
                        <FiLink className="me-2" />
                        Configuration
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">URL to encode</Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="https://example.com"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          className="form-control-lg"
                        />
                        <Form.Text className="text-muted">
                          Enter any valid URL to generate a QR code
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">
                          <FiImage className="me-2" />
                          Overlay Image (Optional)
                        </Form.Label>
                        <div
                          className="upload-area"
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          {overlayImageUrl ? (
                            <div>
                              <div className="d-flex justify-content-center mb-2">
                                <img
                                  src={overlayImageUrl}
                                  alt="Overlay preview"
                                  style={{ 
                                    width: '80px',
                                    height: '80px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '2px solid #dee2e6'
                                  }}
                                />
                              </div>
                              <p className="mb-2 text-success">Image loaded successfully</p>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearOverlay();
                                }}
                              >
                                Remove Image
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <FiImage size={32} className="text-muted mb-2" />
                              <p className="mb-0">Click to upload or drag & drop an image</p>
                              <small className="text-muted">PNG, JPG, GIF up to 10MB</small>
                            </div>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                      </Form.Group>

                      <div className="d-grid gap-2">
                        <Button
                          className="btn-generate"
                          size="lg"
                          onClick={generateQRCode}
                          disabled={isGenerating || !url.trim()}
                        >
                          {isGenerating ? 'Generating...' : 'Generate QR Code'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Header className="bg-transparent border-0 pb-0">
                      <h5 className="mb-0">QR Code Preview</h5>
                    </Card.Header>
                    <Card.Body className="text-center">
                      <canvas
                        ref={canvasRef}
                        className="qr-canvas mb-3"
                        style={{ 
                          maxWidth: '100%', 
                          height: 'auto',
                          display: qrCodeDataUrl ? 'block' : 'none'
                        }}
                        onContextMenu={(e) => {
                          // Allow right-click for saving
                          e.stopPropagation();
                        }}
                      />
                      
                      {qrCodeDataUrl ? (
                        <div>
                          <div className="d-grid gap-2">
                            <Button
                              variant="success"
                              onClick={downloadQRCode}
                              className="d-flex align-items-center justify-content-center"
                            >
                              <FiDownload className="me-2" />
                              Download QR Code
                            </Button>
                            {user && (
                              <Button
                                variant="primary"
                                onClick={saveQRCodeToAPI}
                                disabled={isSaving}
                                className="d-flex align-items-center justify-content-center"
                              >
                                {isSaving ? (
                                  <>
                                    <Spinner size="sm" className="me-2" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <FiSave className="me-2" />
                                    Save to API
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                          <small className="text-muted d-block mt-2">
                            Right-click on the QR code to save it directly
                            {user && <><br />Logged in: Use "Save to API" to store in file manager</>}
                          </small>
                        </div>
                      ) : (
                        <div className="text-muted py-5">
                          <div className="mb-3" style={{ fontSize: '4rem', opacity: 0.3 }}>
                            📱
                          </div>
                          <p>Enter a URL above to generate your QR code</p>
                          {isGenerating && (
                            <div className="mt-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Generating...</span>
                              </div>
                              <p className="mt-2">Generating QR code...</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default App;
